import React from 'react'
import Helmet from './Helmet'

const DEFAULT = {
  titleBase: '100Eyes Technologies',
  description:
    '100Eyes Technologies delivers enterprise-grade software, real-time analytics, and cloud solutions that help businesses scale securely.',
  // Use the canonical production domain (Cloudflare / HTTPS)
  siteUrl: 'https://100eyestechnologies.com',
  defaultImage: 'https://100eyestechnologies.com/img/logo.webp',
}

/**
 * SEO wrapper using the project's Helmet helper.
 * Props:
 * - title: page title (string)
 * - description: meta description (string)
 * - canonical: canonical URL (string)
 * - image: open graph image URL (string)
 * - children: optional extra tags
 */
export default function SEO({ title, description, canonical, image, keywords, children, noIndex = false }) {
  const metaTitle = title ? `${title} | ${DEFAULT.titleBase}` : DEFAULT.titleBase
  const metaDescription = description || DEFAULT.description
  const metaKeywords = Array.isArray(keywords) ? keywords.join(', ') : keywords || ''
  const url = canonical || (typeof window !== 'undefined' ? window.location.href : DEFAULT.siteUrl)
  const imageUrl = image || DEFAULT.defaultImage

  const robotsContent = noIndex ? 'noindex,nofollow' : 'index,follow'

  // Basic structured data: Organization + WebSite
  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: DEFAULT.titleBase,
    url: DEFAULT.siteUrl,
    logo: DEFAULT.defaultImage,
  }

  const siteLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: DEFAULT.siteUrl,
    name: DEFAULT.titleBase,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${DEFAULT.siteUrl}/?s={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  }

  return (
    <Helmet>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
  {metaKeywords ? <meta name="keywords" content={metaKeywords} /> : null}
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={DEFAULT.titleBase} />
      <meta property="og:image" content={imageUrl} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Structured data */}
      <script type="application/ld+json">{JSON.stringify(orgLd)}</script>
      <script type="application/ld+json">{JSON.stringify(siteLd)}</script>

      {children}
    </Helmet>
  )
}
